import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import { fetchColaboradores } from '@/features/rh/api'
import { fetchGedDocumentos, fetchMeusDocumentos } from './api'

function tone(status?: string) {
  const s = (status || '').toLowerCase()
  if (/(pend|aguard|rejeit|analise)/.test(s)) return 'pendente' as const
  if (/(assin|aprov|ok|conclu|dispon)/.test(s)) return 'ativo' as const
  return 'inativo' as const
}

export function DocumentosPage() {
  const { user, isRH, canEdit, perfil } = useAuth()
  const colabId = user?.colaborador_id ? String(user.colaborador_id) : ''
  const isGedUser =
    isRH || canEdit('documentos') || ['admin', 'rh', 'analista'].includes(perfil)

  const [modo, setModo] = useState<'ged' | 'meus'>(() =>
    isGedUser && !colabId ? 'ged' : isGedUser ? 'ged' : 'meus',
  )
  const [busca, setBusca] = useState('')
  const [q, setQ] = useState('')
  const [alvoId, setAlvoId] = useState(colabId)
  const [pickerQ, setPickerQ] = useState('')

  const ged = useQuery({
    queryKey: ['documentos', 'ged', q, alvoId],
    queryFn: () =>
      fetchGedDocumentos({
        busca: q || undefined,
        colaboradorId: alvoId || undefined,
        limit: 80,
      }),
    enabled: modo === 'ged' && isGedUser,
  })

  const meus = useQuery({
    queryKey: ['documentos', 'meus', alvoId || colabId],
    queryFn: () =>
      fetchMeusDocumentos({
        colaboradorId: alvoId && alvoId !== colabId ? alvoId : undefined,
      }),
    enabled: modo === 'meus' && Boolean(alvoId || colabId),
  })

  const picker = useQuery({
    queryKey: ['documentos', 'picker', pickerQ],
    queryFn: () =>
      fetchColaboradores({
        busca: pickerQ || undefined,
        status: 'ativo',
        page: 1,
        por_pagina: 12,
      }),
    enabled: isGedUser && pickerQ.length >= 2,
  })

  const docsQuery = modo === 'ged' ? ged : meus
  const lista = docsQuery.data?.dados || []
  const precisaEscolher = isGedUser && modo === 'meus' && !alvoId && !colabId

  return (
    <section className="hi-page">
      <PageHeader
        title="Documentos"
        subtitle={
          isGedUser
            ? 'GED corporativo via API. Anexos em lote e assinatura avançada no legado.'
            : 'Seus documentos via API. Portal completo no legado.'
        }
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/documentos')}>
            GED legado
          </a>
        }
      />

      {isGedUser ? (
        <form
          className="hi-filters"
          onSubmit={(e) => {
            e.preventDefault()
            setQ(busca.trim())
          }}
        >
          <select
            value={modo}
            onChange={(e) => setModo(e.target.value as 'ged' | 'meus')}
            aria-label="Modo"
          >
            <option value="ged">GED (todos)</option>
            <option value="meus">Por colaborador</option>
          </select>
          {modo === 'ged' ? (
            <input
              type="search"
              placeholder="Buscar título, colaborador, tipo…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          ) : (
            <input
              type="search"
              placeholder="Buscar colaborador (nome)…"
              value={pickerQ}
              onChange={(e) => setPickerQ(e.target.value)}
            />
          )}
          {modo === 'ged' ? (
            <button type="submit" className="btn btn-primary">
              Buscar
            </button>
          ) : null}
          {alvoId ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setAlvoId(colabId)
                setPickerQ('')
              }}
            >
              Limpar filtro
            </button>
          ) : null}
        </form>
      ) : null}

      {modo === 'meus' && isGedUser && pickerQ.length >= 2 ? (
        <Section title="Selecionar colaborador" flush>
          {picker.isLoading ? <Muted>Buscando…</Muted> : null}
          <ul className="hi-list">
            {(picker.data?.dados || []).map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="card card-interactive hi-row"
                  style={{ width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer' }}
                  onClick={() => {
                    setAlvoId(c.id)
                    setPickerQ('')
                    setModo('meus')
                  }}
                >
                  <div className="hi-row-main">
                    <strong>{c.nome}</strong>
                    <span>
                      {c.cargo_nome || 'Sem cargo'}
                      {c.departamento_nome ? ` · ${c.departamento_nome}` : ''}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {!picker.isLoading && (picker.data?.dados || []).length === 0 ? (
            <Empty>Nenhum colaborador encontrado.</Empty>
          ) : null}
        </Section>
      ) : null}

      {!isGedUser && !colabId ? (
        <Alert tone="info">
          Sua conta ainda não está vinculada a um colaborador. Peça ao RH para associar o e-mail{' '}
          <strong>{user?.email || 'desta conta'}</strong> ao cadastro de pessoas.
        </Alert>
      ) : null}

      {precisaEscolher ? (
        <Alert tone="info">
          Conta RH sem colaborador próprio. Use o GED (todos) ou busque um colaborador acima.
        </Alert>
      ) : null}

      <KpiGrid>
        <Kpi label="Documentos" value={String(lista.length)} />
        <Kpi label="Pendentes" value={String(docsQuery.data?.pendentes ?? 0)} />
      </KpiGrid>

      <Section
        title={
          modo === 'ged'
            ? 'GED recente'
            : alvoId && alvoId !== colabId
              ? 'Documentos do colaborador'
              : 'Meus documentos'
        }
        flush
        actions={
          alvoId && alvoId !== colabId ? (
            <Link className="btn btn-secondary btn-sm" to={`/app/rh/${alvoId}`}>
              Ver ficha
            </Link>
          ) : null
        }
      >
        {docsQuery.isLoading ? <Muted>Carregando…</Muted> : null}
        {docsQuery.isError ? (
          <Alert tone="danger">
            {docsQuery.error instanceof ApiError
              ? docsQuery.error.message
              : 'Falha ao listar documentos.'}
          </Alert>
        ) : null}
        {!docsQuery.isLoading && !docsQuery.isError && lista.length === 0 && !precisaEscolher ? (
          <Empty>Nenhum documento encontrado.</Empty>
        ) : null}
        {lista.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                {modo === 'ged' ? <th>Colaborador</th> : null}
                <th>Título</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Criado</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((d) => (
                <tr key={d.id}>
                  {modo === 'ged' ? <td>{d.colaborador_nome || '—'}</td> : null}
                  <td>{d.titulo || '—'}</td>
                  <td>
                    {typeof d.tipo === 'object' && d.tipo
                      ? d.tipo.nome
                      : d.tipo_nome || String(d.tipo || '—')}
                  </td>
                  <td>
                    <StatusBadge tone={tone(d.status)}>
                      {d.status_display || d.status || '—'}
                    </StatusBadge>
                  </td>
                  <td>
                    {d.created_at
                      ? new Date(d.created_at).toLocaleDateString('pt-BR')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}
